import { z } from 'zod';
import { apiClient } from '../lib/apiClient';
import { envelopeSchema, oauthConfigSchema } from '../lib/schemas';
import { logger } from '../utils/logger';
import { API_CONFIG } from './api';

type OAuthConfig = z.infer<typeof oauthConfigSchema>;

let oauthConfigCache: OAuthConfig | null = null;
let cacheExpiry: number = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export async function getOAuthConfig(): Promise<OAuthConfig> {
  const now = Date.now();

  if (oauthConfigCache && now < cacheExpiry) {
    logger.debug('[OAuthConfig] Using cached OAuth configuration');
    return oauthConfigCache;
  }

  try {
    logger.debug('[OAuthConfig] Fetching OAuth configuration from backend');
    const response = await apiClient.get(`${API_CONFIG.apiVersion}/config/oauth`, {
      validate: envelopeSchema('oauth_config', oauthConfigSchema),
      deduplicate: true,
      context: 'OAuthConfig',
    });

    if (!response.oauth_config) {
      throw new Error('OAuth configuration missing from response');
    }

    oauthConfigCache = response.oauth_config;
    cacheExpiry = now + CACHE_TTL;
    logger.debug('[OAuthConfig] OAuth configuration cached');

    return response.oauth_config;
  } catch (error) {
    logger.error('[OAuthConfig] Failed to fetch OAuth configuration:', error);
    throw error;
  }
}

export function clearOAuthConfigCache(): void {
  oauthConfigCache = null;
  cacheExpiry = 0;
  logger.debug('[OAuthConfig] Cache cleared');
}
